import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Drawer } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HomeOutlined,
  FileTextOutlined,
  UserOutlined,
  LogoutOutlined,
  DashboardOutlined,
  FallOutlined,
  ApartmentOutlined,
  BankOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Residences from './Residences';
import Agreements from './Agreements';
import Employees from './Employees';
import DashboardHome from './DashboardHome';
import RoomAllocation from './RoomAllocation';
import AdvanceRefunds from './AdvanceRefunds';
import UnitSettings from './UnitSettings';
import AlertsBell from './AlertsBell';
import Attrition from './Attrition';
import { useResponsive } from '../utils/useResponsive';
import '../App.css';

const { Header, Sider, Content } = Layout;

const Dashboard = () => {
  const responsive = useResponsive();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [agreementsFilter, setAgreementsFilter] = useState(null);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Sync selectedKey with current route
  const getSelectedKeyFromPath = (pathname) => {
    if (pathname.startsWith('/agreements')) return 'agreements';
    if (pathname.startsWith('/employees'))  return 'employees';
    if (pathname.startsWith('/residences')) return 'residences';
    if (pathname.startsWith('/rooms') || pathname.startsWith('/beds')) return 'rooms';
    if (pathname.startsWith('/refunds'))    return 'refunds';
    if (pathname.startsWith('/units'))      return 'units';
    if (pathname.startsWith('/analytics'))  return 'analytics';
    if (pathname.startsWith('/attrition'))  return 'attrition';
    return 'dashboard';
  };

  const [selectedKey, setSelectedKey] = useState(() => getSelectedKeyFromPath(location.pathname));

  // Update selectedKey when route changes
  useEffect(() => {
    const key = getSelectedKeyFromPath(location.pathname);
    setSelectedKey(key);
  }, [location.pathname]);

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    if (responsive.isMobileOrTablet) {
      setCollapsed(true);
    }
  }, [responsive.isMobileOrTablet]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: 'residences',
      icon: <HomeOutlined />,
      label: 'Residences',
    },
    {
      key: 'rooms',
      icon: <ApartmentOutlined />,
      label: 'Rooms & Allocation',
    },
    {
      key: 'agreements',
      icon: <FileTextOutlined />,
      label: 'Agreements',
    },
    {
      key: 'refunds',
      icon: <BankOutlined />,
      label: 'Advance Refunds',
    },
    {
      key: 'employees',
      icon: <UserOutlined />,
      label: 'Employees',
    },
    {
      key: 'attrition',
      icon: <FallOutlined />,
      label: 'Attrition & Retention',
    },
    {
      key: 'units',
      icon: <AppstoreOutlined />,
      label: 'Units',
    },
  ];

  const handleNavigateToAgreements = (filter) => {
    setAgreementsFilter(filter);
    setSelectedKey('agreements');
    if (responsive.isMobileOrTablet) {
      setMobileMenuVisible(false);
    }
    // Navigate to the route so URL matches
    navigate('/agreements' + (filter ? `?filter=${filter}` : ''));
  };

  const handleMenuClick = ({ key }) => {
    setSelectedKey(key);
    if (responsive.isMobileOrTablet) {
      setMobileMenuVisible(false);
    }
    // Navigate to the corresponding route
    const routeMap = {
      'dashboard':  '/dashboard',
      'residences': '/residences',
      'rooms':      '/rooms',
      'refunds':    '/refunds',
      'units':      '/units',
      'agreements': '/agreements',
      'employees':  '/employees',
      'analytics':  '/analytics',
      'attrition':  '/attrition',
    };
    if (routeMap[key]) {
      navigate(routeMap[key]);
    }
  };

  const renderContent = () => {
    switch (selectedKey) {
      case 'residences':
        return <Residences />;
      case 'rooms':
        return <RoomAllocation />;
      case 'refunds':
        return <AdvanceRefunds />;
      case 'units':
        return <UnitSettings />;
      case 'agreements':
        return <Agreements />;
      case 'employees':
        return <Employees />;
      case 'attrition':
        return <Attrition />;
      default:
        return <DashboardHome />;
    }
  };

  // Sidebar menu component (reusable for both desktop and mobile)
  const sidebarMenu = (
    <>
      <div
        style={{
          height: 64,
          margin: 16,
          background: 'linear-gradient(135deg, #E87103 0%, #FF8C00 100%)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#FFFFFF',
          fontWeight: 'bold',
          fontSize: collapsed && !responsive.isMobileOrTablet ? '14px' : '16px',
        }}
      >
        {collapsed && !responsive.isMobileOrTablet ? 'SQM' : 'Soliflex Quarters'}
      </div>
      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[selectedKey]}
        items={menuItems}
        onClick={handleMenuClick}
      />
    </>
  );

  return (
    <Layout className="dashboard-layout" style={{ minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      {!responsive.isMobileOrTablet && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={250}
          collapsedWidth={80}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            background: '#FFFFFF',
            borderRight: '1px solid #D9D9D9',
            zIndex: 100,
          }}
        >
          {sidebarMenu}
        </Sider>
      )}

      {/* Mobile Drawer */}
      {responsive.isMobileOrTablet && (
        <Drawer
          title="Soliflex Quarters"
          placement="left"
          onClose={() => setMobileMenuVisible(false)}
          open={mobileMenuVisible}
          bodyStyle={{ padding: 0 }}
          width={responsive.isMobile ? 280 : 300}
        >
          {sidebarMenu}
        </Drawer>
      )}

      <Layout 
        style={{ 
          marginLeft: responsive.isMobileOrTablet ? 0 : (collapsed ? 80 : 250), 
          transition: 'margin-left 0.2s',
          background: '#F0F2F5',
          minHeight: '100vh',
        }}
      >
        <Header
          style={{
            padding: responsive.isMobile ? '0 12px' : responsive.isTablet ? '0 16px' : '0 24px',
            background: '#FFFFFF',
            borderBottom: '1px solid #E0E0E0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            position: 'sticky',
            top: 0,
            zIndex: 99,
            height: responsive.isMobile ? 56 : 64,
          }}
        >
          <Button
            type="text"
            icon={responsive.isMobileOrTablet ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
            onClick={() => {
              if (responsive.isMobileOrTablet) {
                setMobileMenuVisible(true);
              } else {
                setCollapsed(!collapsed);
              }
            }}
            style={{
              fontSize: '16px',
              width: responsive.isMobile ? 40 : responsive.isTablet ? 48 : 64,
              height: responsive.isMobile ? 40 : responsive.isTablet ? 48 : 64,
              color: '#262626',
            }}
          />
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: responsive.isMobile ? '8px' : responsive.isTablet ? '12px' : '16px',
            flexWrap: 'wrap',
          }}>
            <AlertsBell compact={responsive.isMobile} />
            {!responsive.isMobile && (
              <span style={{ color: '#262626', fontWeight: 500, fontSize: responsive.isTablet ? '13px' : '14px' }}>
                Welcome, {user?.username || 'Admin'}
              </span>
            )}
            <Button
              type="primary"
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              size={responsive.isMobile ? 'small' : 'middle'}
            >
              {responsive.isMobile ? '' : 'Logout'}
            </Button>
          </div>
        </Header>
        <Content 
          className="dashboard-content"
          style={{
            padding: responsive.isMobile ? '12px' : responsive.isTablet ? '16px' : '24px',
            minHeight: 'calc(100vh - 64px)',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
          }}
        >
          {renderContent()}
        </Content>
      </Layout>
    </Layout>
  );
};

export default Dashboard;
